import ClubNavigation from '@/components/ClubNavigation'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/auth'
import { Icon, ICONS } from '@/lib/icons'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

interface LayoutProps {
  children: ReactNode
}

export function Layout({ children }: LayoutProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { user, isAuthenticated, logout } = useAuthStore()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const navigation = [
    {
      name: t('nav.series'),
      href: '/series',
      icon: ICONS.tournament,
      current: location.pathname === '/series'
    },
    {
      name: t('nav.clubs'),
      href: '/clubs',
      icon: ICONS.buildings,
      current: location.pathname === '/clubs'
    },
    {
      name: t('nav.players'),
      href: '/players',
      icon: ICONS.users,
      current: location.pathname === '/players'
    },
    {
      name: t('nav.leaderboard'),
      href: '/leaderboard',
      icon: ICONS.podium,
      current: location.pathname === '/leaderboard'
    },
    {
      name: t('nav.manual'),
      href: '/manual',
      icon: ICONS.clipboard,
      current: location.pathname === '/manual'
    }
  ]

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="border-b border-border bg-card/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-2">
                <Icon icon={ICONS.trophy} width={32} height={32} className="text-primary" />
                <span className="text-xl font-bold text-foreground">Klubbspel</span>
              </Link>
            </div>

            {/* Navigation and Club Selector */}
            <div className="flex items-center space-x-4">
              <nav className="hidden md:flex space-x-1">
                {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        'flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                        item.current
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      )}
                    >
                      <Icon icon={item.icon} width={18} height={18} className="text-current" />
                      <span>{item.name}</span>
                    </Link>
                ))}
              </nav>

              {/* Club Navigation - only show if authenticated */}
              {isAuthenticated() && user && (
                <div className="hidden lg:block">
                  <ClubNavigation 
                    showAllOption={true} 
                    onClubChange={(_clubId) => {
                      // If we're on a specific club page and user changes club selection,
                      // navigate to clubs list page to avoid confusion
                      if (location.pathname.startsWith('/clubs/') && location.pathname !== '/clubs') {
                        navigate('/clubs')
                      }
                    }}
                  />
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              {isAuthenticated() && user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center space-x-2">
                      <Icon icon={ICONS.user} width={18} height={18} />
                      <span className="hidden sm:inline">{user.displayName}</span>
                      <Icon icon={ICONS.arrowDown} width={16} height={16} className="hidden sm:inline" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <Link to="/settings" className="flex items-center space-x-2">
                        <Icon icon={ICONS.edit} width={16} height={16} />
                        <span>{t('nav.settings')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="flex items-center space-x-2">
                      <Icon icon={ICONS.logout} width={16} height={16} />
                      <span>{t('auth.signOut')}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button asChild>
                  <Link to="/login">{t('auth.signIn')}</Link>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden border-t border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-1 py-2 overflow-x-auto">
              {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      'flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors',
                      item.current
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    )}
                  >
                    <Icon icon={item.icon} width={18} height={18} className="text-current" />
                    <span>{item.name}</span>
                  </Link>
              ))}

              {/* Club Navigation for mobile - show if authenticated and on clubs page */}
              {isAuthenticated() && user && location.pathname === '/clubs' && (
                <div className="flex items-center ml-2">
                  <ClubNavigation 
                    showAllOption={true} 
                    onClubChange={(_clubId) => {
                      // If we're on a specific club page and user changes club selection,
                      // navigate to clubs list page to avoid confusion
                      if (location.pathname.startsWith('/clubs/') && location.pathname !== '/clubs') {
                        navigate('/clubs')
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  )
}